import { Link as RouterLink } from 'react-router-dom';
import { Badge, Box, chakra, HStack, Text } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import type { IconType } from 'react-icons';
import type { ReactNode } from 'react';
import { Tooltip } from '@/components/ui/tooltip';
import { SIDEBAR_ROW } from '../layoutTokens';

export interface SidebarNavItemProps {
    icon: IconType;
    label: string;
    to: string;
    active?: boolean;
    collapsed?: boolean;
    badge?: number;
    external?: boolean;
    onNavigate?: () => void;
    endAction?: ReactNode;
}

export function SidebarNavItem({
    icon: Icon,
    label,
    to,
    active = false,
    collapsed = false,
    badge,
    external = false,
    onNavigate,
    endAction,
}: SidebarNavItemProps) {
    const content = (
        <HStack
            gap={SIDEBAR_ROW.gap}
            w="full"
            px={collapsed ? 0 : SIDEBAR_ROW.px}
            pe={!collapsed && endAction ? 10 : undefined}
            py={SIDEBAR_ROW.py}
            justifyContent={collapsed ? 'center' : 'flex-start'}
            borderRadius={SIDEBAR_ROW.radius}
            bg={active ? 'gold.wash' : 'transparent'}
            cursor={'pointer'}
            opacity={1}
            transition="background 0.15s ease"
            _hover={{ bg: active ? 'gold.wash' : 'bg.rowHover' }}
        >
            <Box
                flexShrink={0}
                color={active ? 'gold.ink' : 'fg.nav'}
                opacity={active ? 1 : 0.85}
                aria-hidden
                position="relative"
                display="flex"
            >
                <Icon size={SIDEBAR_ROW.iconSize} strokeWidth={active ? 1.7 : 1.5} />

                {collapsed && badge !== undefined && badge > 0 && (
                    <Box
                        position="absolute"
                        top="-2px"
                        right="-3px"
                        w="1.5"
                        h="1.5"
                        borderRadius="full"
                        bg="gold.solid"
                        borderWidth="1.5px"
                        borderColor="bg.canvas"
                    />
                )}
            </Box>

            {!collapsed && (
                <>
                    <Text
                        flex={1}
                        minW={0}
                        truncate
                        fontSize="14px"
                        fontWeight={active ? 'semibold' : 'medium'}
                        color={active ? 'fg' : 'fg.nav'}
                    >
                        {label}
                    </Text>

                    {badge !== undefined && badge > 0 && (
                        <Badge colorPalette="gold" variant="subtle" fontSize="10px" px={1.5} py={0} borderRadius="full">
                            {badge > 99 ? (
                                <FormattedMessage
                                    id="6a76356d-249a-456c-aece-7ca31ac50dd0"
                                    defaultMessage="99+"
                                />
                            ) : (
                                badge
                            )}
                        </Badge>
                    )}
                </>
            )}
        </HStack>
    );

    const focusRing = {
        outline: '2px solid',
        outlineColor: 'gold.solid',
        outlineOffset: '1px',
    };

    const row = external ? (
        <chakra.a
            href={to}
            target="_blank"
            rel="noreferrer"
            aria-label={collapsed ? label : undefined}
            display="block"
            w="full"
            borderRadius={SIDEBAR_ROW.radius}
            _focusVisible={focusRing}
        >
            {content}
        </chakra.a>
    ) : (
        <RouterLink
            to={to}
            onClick={onNavigate}
            aria-current={active ? 'page' : undefined}
            aria-label={collapsed ? label : undefined}
            style={{ display: 'block', width: '100%' }}
        >
            {content}
        </RouterLink>
    );

    if (!collapsed) {
        if (!endAction) return row;

        return (
            <Box position="relative">
                {row}
                <Box
                    position="absolute"
                    insetEnd="1"
                    top="50%"
                    transform="translateY(-50%)"
                    display="flex"
                >
                    {endAction}
                </Box>
            </Box>
        );
    }

    const tooltipLabel = badge !== undefined && badge > 0 ? `${label} (${badge > 99 ? '99+' : badge})` : label;

    return (
        <Tooltip content={tooltipLabel} positioning={{ placement: 'right' }} openDelay={200}>
            {row}
        </Tooltip>
    );
}
